import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { SitePasswordGuard } from './core/guards/site-password.guard';
import { DemoGateGuard } from './core/guards/demo-gate.guard';
import { AdminRoleGuard } from './core/guards/admin-role.guard';
import { ReservationFeatureGuard } from './core/guards/reservation-feature.guard';

const routes: Routes = [
  // Demo gate page - single password unlocks the whole site for the session.
  // Active only when environment.demoGatePassword is set. No guard on this
  // route so users can always reach it to enter the password.
  {
    path: 'demo-unlock',
    loadComponent: () => import('./features/public/demo-gate/demo-gate.component').then(m => m.DemoGateComponent)
  },
  // Password gate page (no guard)
  {
    path: 'password',
    loadComponent: () => import('./features/public/password-gate/password-gate.component').then(m => m.PasswordGateComponent)
  },
  // Permit approver landing page (no auth — JWT from email serves as Bearer token)
  // Must be before the '' PublicModule catch-all to avoid prefix-match swallowing it
  {
    path: 'permit/approver',
    loadComponent: () => import('./features/public/permit-approver/permit-approver.component').then(m => m.PermitApproverComponent)
  },
  // Tax credit approver landing page (no auth — JWT from email serves as Bearer token)
  {
    path: 'tax/publicview',
    loadComponent: () => import('./features/public/tax-publicview/tax-publicview.component').then(m => m.TaxPublicviewComponent)
  },
  // Backward-compat: singular /lookbook/:id → plural /lookbooks/:id
  {
    path: 'lookbook/:id',
    redirectTo: 'lookbooks/:id'
  },
  // Public routes (includes reservations). DemoGateGuard runs first so even
  // public pages require the demo password when one is configured.
  // SitePasswordGuard gates EVERY public page when the site has a gate
  // password set (Dave 2026-07-08: "password should work on every public
  // url/page but once I enter it, opens all") — one verified entry unlocks
  // the whole session. The guard lets /password itself through.
  {
    path: '',
    canActivate: [DemoGateGuard, SitePasswordGuard],
    loadChildren: () => import('./features/public/public.module').then(m => m.PublicModule)
  },
  // Admin routes - guarded
  {
    path: 'admin',
    canActivate: [DemoGateGuard, SitePasswordGuard, AdminRoleGuard],
    canActivateChild: [AdminRoleGuard],
    loadChildren: () => import('./features/admin/admin.module').then(m => m.AdminModule)
  },
  // Projects routes (alias for admin — includes budget/view/:id)
  {
    path: 'projects',
    canActivate: [DemoGateGuard, SitePasswordGuard, AdminRoleGuard],
    canActivateChild: [AdminRoleGuard],
    loadChildren: () => import('./features/admin/admin.module').then(m => m.AdminModule)
  },
  // Location routes - guarded
  {
    path: 'locations',
    canActivate: [DemoGateGuard, SitePasswordGuard],
    loadChildren: () => import('./features/locations/locations.module').then(m => m.LocationsModule)
  },
  // Lookbook routes - guarded
  {
    path: 'lookbooks',
    canActivate: [DemoGateGuard, SitePasswordGuard],
    loadChildren: () => import('./features/lookbooks/lookbooks.module').then(m => m.LookbooksModule)
  },
  // E-Sign route (public, standalone) - guarded
  {
    path: 'sign/:token',
    canActivate: [SitePasswordGuard],
    loadComponent: () => import('./features/public/sow-sign/sow-sign.component').then(m => m.SowSignComponent)
  },
  // Shared analytics (public, password-gated via token)
  {
    path: 'analytics/shared/:token',
    loadComponent: () => import('./features/public/shared-analytics/shared-analytics.component').then(m => m.SharedAnalyticsComponent)
  },
  // Shared budget (public, password-gated via ?token= query param)
  {
    path: 'budget/shared',
    loadComponent: () => import('./features/public/shared-budget/shared-budget.component').then(m => m.SharedBudgetComponent)
  },
  // Short share link (piece 2 of the LP budget upgrade, 2026-09-23,
  // budget-upgrade sites only): /s/<code> resolves to a signed share token
  // then hands off to budget/shared above. No guard: the code alone carries
  // no site-password bypass, and the backend 404s off-flag or on an
  // unknown/expired code.
  {
    path: 's/:code',
    loadComponent: () => import('./features/public/short-share-link/short-share-link.component').then(m => m.ShortShareLinkComponent)
  },
  // Shared Accounting overview (public, password-gated via token; the URL
  // shape the build brief specifies: /accounting/:token). No guard: tenant
  // identity is resolved server-side from the token alone (accounting-
  // share.ts), so this must be reachable regardless of any site password
  // gate — the same reasoning as legal/review below.
  {
    path: 'accounting/:token',
    loadComponent: () => import('./features/public/shared-accounting/shared-accounting.component').then(m => m.SharedAccountingComponent)
  },
  // Online permit (public, no login). The QR code on every approved permit PDF
  // points here: https://{site host}/permit-view/<permitId>.<token>. The ref is
  // the only credential and the server decides validity. No guard: the officer
  // scanning it on set has no account and must never meet the site-password,
  // login-wall or demo gate. Must stay above the '' PublicModule catch-all.
  {
    path: 'permit-view/:ref',
    loadComponent: () => import('./features/public/permit-view/permit-view.component').then(m => m.PermitViewComponent)
  },
  // KY legal review (public, no LP account — token-authenticated via ?token=
  // query param). Matches the link path lp-vx's triggerLegalReview mints:
  // https://{host}/legal/review?token=... (register/legal-approval.ts).
  // No guard: the legal reviewer has no site session and must never hit the
  // site-password or demo gate.
  {
    path: 'legal/review',
    loadComponent: () => import('./features/public/legal-review/legal-review.component').then(m => m.LegalReviewComponent)
  },
  // Shared calendar (public, password-gated via token)
  {
    path: 'calendar/shared/:token',
    loadComponent: () => import('./features/public/shared-calendar/shared-calendar.component').then(m => m.SharedCalendarComponent)
  },
  // Shared permit calendar (public, password-gated via token)
  {
    path: 'permit-calendar/shared/:token',
    loadComponent: () => import('./features/public/shared-permit-calendar/shared-permit-calendar.component').then(m => m.SharedPermitCalendarComponent)
  },
  // Shared permit analytics (public, password-gated via token; read-only)
  {
    path: 'permit-analytics/shared/:token',
    loadComponent: () => import('./features/public/shared-permit-analytics/shared-permit-analytics.component').then(m => m.SharedPermitAnalyticsComponent)
  },
  // Shared permit spending (public, password-gated via server-signed token; read-only)
  {
    path: 'spending/shared/:token',
    loadComponent: () => import('./features/public/shared-spending/shared-spending.component').then(m => m.SharedSpendingComponent)
  },
  // Auth routes - guarded
  {
    path: 'login',
    canActivate: [DemoGateGuard, SitePasswordGuard],
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  // Email verification - guarded
  {
    path: 'verify-email',
    canActivate: [DemoGateGuard, SitePasswordGuard],
    loadChildren: () => import('./features/auth/verify-email.module').then(m => m.VerifyEmailModule)
  },
  // Reservations routes - guarded
  {
    path: 'reservations',
    canActivate: [DemoGateGuard, SitePasswordGuard, ReservationFeatureGuard],
    loadChildren: () => import('./features/public/reservations/reservations.module').then(m => m.ReservationsModule)
  },
  // Password reset / invite link handler
  {
    path: 'reset-password',
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  // Set password via invite email link
  {
    path: 'set-password',
    loadComponent: () => import('./features/public/set-password/set-password.component').then(m => m.SetPasswordComponent)
  },
  // Wildcard
  {
    path: '**',
    redirectTo: ''
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    scrollPositionRestoration: 'enabled'
  })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
